import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { getAccessToken } from '../store/actions/auth';
import API_BASE_URL from '../config/baseUrl';
import { formatDisplayUserId } from '../utils/userIdFormat';

// Fetch complete ledger v2 data
const fetchCompleteLedgerV2 = async function(matchId, userId = "") {
  try {
    const token = getAccessToken();
    
    if (!token) {
      return { success: false, error: "No access token found" };
    }

    const response = await axios.post(
      API_BASE_URL + "/report/get-complete-ledger-v2",
      {
        matchId: matchId,
        userId: userId
      },
      { headers: { Authorization: "Bearer " + token } }
    );

    const data = response.data;

    if (data.status === false) {
      return { success: false, error: data.message || "Failed to fetch data" };
    }

    return { success: true, data: data.data };
  } catch (err) {
    const errorMessage = err.response?.data?.message || "Failed to fetch data";
    return { success: false, error: errorMessage };
  }
};

const CompanyReportPage = () => {
  const navigate = useNavigate();
  const { matchId, matchName } = useParams();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [reportData, setReportData] = useState([]);
  
  // Drilldown state - tracks navigation history
  const [drilldownHistory, setDrilldownHistory] = useState([]);
  const [currentUserId, setCurrentUserId] = useState("");
  const [currentUserName, setCurrentUserName] = useState("");
  const formatCode = function(userId) {
    return formatDisplayUserId(userId);
  };

  useEffect(function() {
    const loadData = async function() {
      setLoading(true);
      setError(null);

      const result = await fetchCompleteLedgerV2(matchId, currentUserId);

      if (result.success) {
        setReportData(result.data || []);
      } else {
        setError(result.error);
      }

      setLoading(false);
    };

    if (matchId) {
      loadData();
    }
  }, [matchId, currentUserId]);

  // Handle clicking on a user code to drill down
  const handleUserClick = function(userId, userName) {
    // Check if this user is a Client (starts with 'C')
    // Clients are the bottom level, so we don't drill down further
    const isClient = userId && userId.startsWith('C');
    
    if (isClient) {
      // At Client level - do nothing or show a message
      return;
    }
    
    // Add current state to history before drilling down
    setDrilldownHistory([...drilldownHistory, { userId: currentUserId, userName: currentUserName }]);
    setCurrentUserId(userId);
    setCurrentUserName(userName);
  };

  // Handle going back up one level
  const handleGoBack = function() {
    if (drilldownHistory.length > 0) {
      const previous = drilldownHistory[drilldownHistory.length - 1];
      setCurrentUserId(previous.userId);
      setCurrentUserName(previous.userName);
      setDrilldownHistory(drilldownHistory.slice(0, -1));
    }
  };


  // Calculate totals
  const calculateTotals = function() {
    if (!reportData || reportData.length === 0) {
      return {
        matchAmount: 0,
        sessionAmount: 0,
        total: 0,
        matchComm: 0,
        sessionComm: 0,
        totalComm: 0,
        totalAmount: 0,
        myShare: 0,
        mapp: 0,
        netAmount: 0
      };
    }

    return reportData.reduce(function(totals, item) {
      return {
        matchAmount: totals.matchAmount + (item.matchAmount || 0),
        sessionAmount: totals.sessionAmount + (item.sessionAmount || 0),
        total: totals.total + (item.total || 0),
        matchComm: totals.matchComm + (item.matchComm || 0),
        sessionComm: totals.sessionComm + (item.sessionComm || 0),
        totalComm: totals.totalComm + (item.totalComm || 0),
        totalAmount: totals.totalAmount + (item.totalAmount || 0),
        myShare: totals.myShare + (item.myShare || 0),
        mapp: totals.mapp + (item.mapp || 0),
        netAmount: totals.netAmount + (item.netAmount || 0)
      };
    }, {
      matchAmount: 0,
      sessionAmount: 0,
      total: 0,
      matchComm: 0,
      sessionComm: 0,
      totalComm: 0,
      totalAmount: 0,
      myShare: 0,
      mapp: 0,
      netAmount: 0
    });
  };

  const totals = calculateTotals();

  return (
    <div className="w-full">
      <div className="flex justify-center">
        <div className="w-full px-2">
          <div className="bg-white rounded shadow border border-gray-200">
            {/* Header */}
            <div className="bg-gray-600 px-5 py-3 flex items-center justify-between">
              <div>
                <span className="text-xl text-white font-normal">Company Report</span>
                <p className="text-lg text-white mt-1">
                  {decodeURIComponent(matchName || '')}
                </p>
                {/* Show current drilldown level */}
                {currentUserName && (
                  <p className="text-sm text-gray-200 mt-1">
                    Showing downline for: <span className="font-semibold">{currentUserName}</span> ({currentUserId})
                  </p>
                )}
              </div>
              <div className="flex gap-2">
                <button 
                  onClick={function() { navigate(-1); }}
                  className="bg-blue-500 text-white px-4 py-2 hover:bg-blue-600"
                  style={{ borderRadius: 0 }}
                >
                  Close
                </button>
              </div>
            </div>

            {/* Table */}
            <div className="p-0">
              <div className="overflow-x-auto">
                {loading ? (
                  <div className="flex justify-center items-center py-12">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
                  </div>
                ) : error ? (
                  <div className="p-4 text-red-500 text-center">{error}</div>
                ) : (
                  <table className="w-full border-collapse text-sm" style={{ minWidth: '100%', tableLayout: 'auto' }}>
                    <thead className="bg-gray-100">
                      <tr>
                        <th className="border border-gray-300 p-2 text-left font-medium">Code</th>
                        <th className="border border-gray-300 p-2 text-left font-medium">Name</th>
                        <th className="border border-gray-300 p-2 text-left font-medium">Match Amt</th>
                        <th className="border border-gray-300 p-2 text-left font-medium">Session Amt</th>
                        <th className="border border-gray-300 p-2 text-left font-medium">Total</th>
                        <th className="border border-gray-300 p-2 text-left font-medium">Match Comm+</th>
                        <th className="border border-gray-300 p-2 text-left font-medium">Session Comm+</th>
                        <th className="border border-gray-300 p-2 text-left font-medium">Total Comm</th>
                        <th className="border border-gray-300 p-2 text-left font-medium">Total Amount</th>
                        <th className="border border-gray-300 p-2 text-left font-medium">My Share</th>
                        <th className="border border-gray-300 p-2 text-left font-medium">M.App</th>
                        <th className="border border-gray-300 p-2 text-left font-medium">Net Amount</th>
                      </tr>
                    </thead>
                    <tbody>
                      {reportData.length === 0 ? (
                        <tr>
                          <td colSpan="12" className="border border-gray-300 p-8">
                            <div className="flex flex-col items-center justify-center text-gray-400">
                              <svg className="w-16 h-16 mb-4" viewBox="0 0 64 41" xmlns="http://www.w3.org/2000/svg">
                                <g transform="translate(0 1)" fill="none" fillRule="evenodd">
                                  <ellipse className="fill-gray-200" cx="32" cy="33" rx="32" ry="7"></ellipse>
                                  <g className="fill-gray-300" fillRule="nonzero">
                                    <path d="M55 12.76L44.854 1.258C44.367.474 43.656 0 42.907 0H21.093c-.749 0-1.46.474-1.947 1.257L9 12.761V22h46v-9.24z"></path>
                                    <path d="M41.613 15.931c0-1.605.994-2.93 2.227-2.931H55v18.137C55 33.26 53.68 35 52.05 35h-40.1C10.32 35 9 33.259 9 31.137V13h11.16c1.233 0 2.227 1.323 2.227 2.928v.022c0 1.605 1.005 2.901 2.237 2.901h14.752c1.232 0 2.237-1.308 2.237-2.913v-.007z"></path>
                                  </g>
                                </g>
                              </svg>
                              <div className="text-gray-500">
                                {currentUserName ? `No downline data for ${currentUserName}` : 'No data'}
                              </div>
                            </div>
                          </td>
                        </tr>
                      ) : (
                        reportData.map(function(item, index) {
                          // Check if this is a Client (bottom level)
                          const isClient = item.userId && item.userId.startsWith('C');
                          
                          return (
                            <tr key={index} className="hover:bg-gray-50">
                              <td className="border border-gray-300 p-2">
                                {isClient ? (
                                  // Client level - not clickable
                                  <span className="font-medium">
                                    {formatCode(item.userId)}
                                  </span>
                                ) : (
                                  // Other levels - clickable
                                  <button
                                    onClick={function() { handleUserClick(item.userId, item.userName); }}
                                    className="text-blue-600 hover:text-blue-800 hover:underline font-medium cursor-pointer"
                                    title="Click to view downline"
                                  >
                                    {formatCode(item.userId)}
                                  </button>
                                )}
                              </td>
                              <td className="border border-gray-300 p-2">{item.userName}</td>
                              <td className="border border-gray-300 p-2 text-right">
                                <span className={item.matchAmount >= 0 ? 'text-green-600' : 'text-red-600'}>
                                  {(item.matchAmount || 0).toFixed(2)}
                                </span>
                              </td>
                              <td className="border border-gray-300 p-2 text-right">
                                <span className={item.sessionAmount >= 0 ? 'text-green-600' : 'text-red-600'}>
                                  {(item.sessionAmount || 0).toFixed(2)}
                                </span>
                              </td>
                              <td className="border border-gray-300 p-2 text-right">
                                <span className={item.total >= 0 ? 'text-green-600' : 'text-red-600'}>
                                  {(item.total || 0).toFixed(2)}
                                </span>
                              </td>
                              <td className="border border-gray-300 p-2 text-right">
                                <span className={item.matchComm >= 0 ? 'text-green-600' : 'text-red-600'}>
                                  {(item.matchComm || 0).toFixed(2)}
                                </span>
                              </td>
                              <td className="border border-gray-300 p-2 text-right">
                                <span className={item.sessionComm >= 0 ? 'text-green-600' : 'text-red-600'}>
                                  {(item.sessionComm || 0).toFixed(2)}
                                </span>
                              </td>
                              <td className="border border-gray-300 p-2 text-right">
                                <span className={item.totalComm >= 0 ? 'text-green-600' : 'text-red-600'}>
                                  {(item.totalComm || 0).toFixed(2)}
                                </span>
                              </td>
                              <td className="border border-gray-300 p-2 text-right">
                                <span className={item.totalAmount >= 0 ? 'text-green-600' : 'text-red-600'}>
                                  {(item.totalAmount || 0).toFixed(2)}
                                </span>
                              </td>
                              <td className="border border-gray-300 p-2 text-right">
                                <span className={item.myShare >= 0 ? 'text-green-600' : 'text-red-600'}>
                                  {(item.myShare || 0).toFixed(2)}
                                </span>
                              </td>
                              <td className="border border-gray-300 p-2 text-right">
                                <span className={item.mapp >= 0 ? 'text-green-600' : 'text-red-600'}>
                                  {(item.mapp || 0).toFixed(2)}
                                </span>
                              </td>
                              <td className="border border-gray-300 p-2 text-right">
                                <span className={item.netAmount >= 0 ? 'text-green-600' : 'text-red-600'}>
                                  {(item.netAmount || 0).toFixed(2)}
                                </span>
                              </td>
                            </tr>
                          );
                        })
                      )}
                    </tbody>
                    <tfoot>
                      <tr style={{ background: 'rgb(213, 220, 102)' }}>
                        <td className="border border-gray-300 p-2 font-bold">Total</td>
                        <td className="border border-gray-300 p-2"></td>
                        <td className="border border-gray-300 p-2 text-right">
                          <span className="font-bold text-green-600">
                            {totals.matchAmount.toFixed(2)}
                          </span>
                        </td>
                        <td className="border border-gray-300 p-2 text-right">
                          <span className="font-bold text-green-600">
                            {totals.sessionAmount.toFixed(2)}
                          </span>
                        </td>
                        <td className="border border-gray-300 p-2 text-right">
                          <span className="font-bold text-green-600">
                            {totals.total.toFixed(2)}
                          </span>
                        </td>
                        <td className="border border-gray-300 p-2 text-right">
                          <span className="font-bold text-green-600">
                            {totals.matchComm.toFixed(2)}
                          </span>
                        </td>
                        <td className="border border-gray-300 p-2 text-right">
                          <span className="font-bold text-green-600">
                            {totals.sessionComm.toFixed(2)}
                          </span>
                        </td>
                        <td className="border border-gray-300 p-2 text-right">
                          <span className="font-bold text-green-600">
                            {totals.totalComm.toFixed(2)}
                          </span>
                        </td>
                        <td className="border border-gray-300 p-2 text-right">
                          <span className="font-bold text-green-600">
                            {totals.totalAmount.toFixed(2)}
                          </span>
                        </td>
                        <td className="border border-gray-300 p-2 text-right">
                          <span className="font-bold text-green-600">
                            {totals.myShare.toFixed(2)}
                          </span>
                        </td>
                        <td className="border border-gray-300 p-2 text-right">
                          <span className="font-bold text-green-600">
                            {totals.mapp.toFixed(2)}
                          </span>
                        </td>
                        <td className="border border-gray-300 p-2 text-right">
                          <span className="font-bold text-green-600">
                            {totals.netAmount.toFixed(2)}
                          </span>
                        </td>
                      </tr>
                    </tfoot>
                  </table>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CompanyReportPage;
